import { AfterViewChecked, AfterViewInit, Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { WorkflowService } from '../../shared/services/workflow.service';
import {
	BehaviorSubject,
	combineLatest,
	combineLatestWith,
	concat,
	concatMap,
	firstValueFrom,
	forkJoin,
	mergeMap,
	Observable,
	of,
	switchMap,
} from 'rxjs';
import { ListOfWorkflowGroup } from '../../shared/interfaces/workflow';
import { catchError, filter, map, take, tap, toArray, withLatestFrom, finalize } from 'rxjs/operators';
import { CalendarOptions } from '@fullcalendar/core';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import { ResourceInput } from '@fullcalendar/resource-common';
import { createElement, EventInput, FullCalendarComponent } from '@fullcalendar/angular';
import { DateSelectArg } from '@fullcalendar/common';
import { MatDialog } from '@angular/material/dialog';
import { TxPlaningPlansDialogComponent } from './tx-planing-plans-dialog/tx-planing-plans-dialog.component';
import { TxPlaningSelectPlanDialogComponent } from './tx-planing-select-plan-dialog/tx-planing-select-plan-dialog.component';
import { Store } from '@ngrx/store';
import { getPlanCalendar, getPlansList } from '../../store/selectors/plans.selectors';
import { PlanCalendarEntity } from '../../store/entities/PlanCalendarEntity';
import { PlansService } from '../../shared/services/plans.service';
import moment from 'moment';
import chroma from 'chroma-js';
import { MDCTooltip } from '@material/tooltip';
import { TxConfirmDialogComponent } from '../../shared/directives/confirm-dialog-click.directive';
import { AppConfigService } from '../../shared/services/appconfig.service';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { ListOfCalendarHead } from '../../shared/interfaces/scheduler';
import { TxApiService } from '../../shared/services/txapi.service';
import { VariableBinding } from '@angular/compiler';
import { TranslateService } from '@ngx-translate/core';
import { GlobalConstants } from '../../globalConstants';
import { ReportEntity } from '../../store/entities/ReportEntity';
import { getReportList } from '../../store/selectors/reports.selectors';
import { TxreportapiService } from '../../shared/services/txreportapi.service';
import { TxModalPdfComponent } from '../tx-modal-pdf/tx-modal-pdf.component';
import { MatDatepicker, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { DeviceDetectorService } from 'ngx-device-detector';
import { CalendarUtilService } from '../../shared/services/calendar-ulti.service';
import { TabStateService } from '../../shared/services/tabstate.service';

@Component({
	selector: 'tx-planing',
	templateUrl: './tx-planing.component.html',
	styleUrls: ['./tx-planing.component.scss'],
})
export class TxPlaningComponent implements OnDestroy, AfterViewInit, OnInit {
	@ViewChild('calendar') calendarComponent: FullCalendarComponent;
	@ViewChild('picker') datePicker: MatDatepicker<Date>;
	dateControl = new FormControl();

	public SelectedGroups: number[] = [];
	public SelectedUsers: number[] = [];
	private eventsSubscription: any = null;
	private pendingCalendarHeadRequests = new Set<string>();
	public workflowGroups$: Observable<Array<ListOfWorkflowGroup>>;
	public workflows$: Observable<PlanCalendarEntity[]>;

	public selectedGroups$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>(null);
	public selectedUsers$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);

	// User filter properties
	public allUsers: Array<{value: number, label: string}> = [];
	public filteredUsersArray: Array<{value: number, label: string}> = [];
	public searchControlUsers = new FormControl();

	public DeleteMode = false;
	public currentDateRangeText = '';
	public SelectedForDelete: string[] = [];

	public MultiSelectUserIds: string[] = [];

	// Add the teamLeaderFor property
	public teamLeaderFor: number = null;
	private headerDataSubject = new BehaviorSubject<{[key: string]: string}>({});
	private isLoadingHeaderData = false;

	// New properties for calendar head data handling
	private calendarHeadData: Map<string, any> = new Map();
	private isCalendarHeadLoading = false;
	private currentViewDateRange: { start: string; end: string } = { start: '', end: '' };

	public CalendarOptions: CalendarOptions = {
		schedulerLicenseKey: GlobalConstants.fullCalendarLicenseKey,
		plugins: [resourceTimelinePlugin],
		locale: this.translate.currentLang.slice(0, 2),
		headerToolbar: {
			left: 'refresh today prevCustom,nextCustom datePicker',
			center: '',
			right: 'viewDay,viewWeek,viewWeek2',
		},
		initialView: 'resourceTimelineDay',
		themeSystem: 'bootstrap',
		resourceOrder: 'title',
		buttonText: {
			today: this.deviceService.isMobile() ? this.translate.instant('txScheduler.todayShort') : this.translate.instant('txScheduler.today'),
		},
		bootstrapFontAwesome: {
			today: 'fa-clock',
		},
		customButtons: {
			datePicker: {
				text: '',
				bootstrapFontAwesome: 'fa-calendar',
				click: () => {
					this.datePicker.open();
				},
			},

			refresh: {
				text: this.deviceService.isMobile() ? '' : this.translate.instant('general.reload'),
				bootstrapFontAwesome: 'fa-sync',
				click: (ev: MouseEvent, element: HTMLElement) => {
					this.resetHeaderData();
					this.calendarComponent.getApi().refetchEvents();
				},
			},

			viewDay: {
				text: this.deviceService.isMobile() ? this.translate.instant('txScheduler.dayShort') : this.translate.instant('txScheduler.day'),
				click: (mouseEvent, htmlElement) => {
					this.resetHeaderData();
					this.calendarComponent.getApi().changeView('resourceTimelineDay');
					this.calendarComponent.getApi().refetchEvents();
					this.updateDateRangeText();

					this.appConfig.planingConfig$.pipe(take(1)).subscribe((config) => {
						config.SelectedView = 'resourceTimelineDay';
						this.appConfig.setPlaningConfig(config);
					});

					htmlElement.parentNode.querySelector('.fc-state-active')?.classList.remove('fc-state-active');
					htmlElement.classList.add('fc-state-active');
				},
			},

			viewWeek: {
				text: this.deviceService.isMobile() ? this.translate.instant('txScheduler.weekShort') : this.translate.instant('txScheduler.week'),
				click: (mouseEvent, htmlElement) => {
					this.resetHeaderData();
					this.calendarComponent.getApi().changeView('resourceTimelineWeek');
					this.calendarComponent.getApi().refetchEvents();
					this.updateDateRangeText();

					this.appConfig.planingConfig$.pipe(take(1)).subscribe((config) => {
						config.SelectedView = 'resourceTimelineWeek';
						this.appConfig.setPlaningConfig(config);
					});

					htmlElement.parentNode.querySelector('.fc-state-active')?.classList.remove('fc-state-active');
					htmlElement.classList.add('fc-state-active');
				},
			},

			viewWeek2: {
				text: this.deviceService.isMobile() ? this.translate.instant('txScheduler.twoWeeksShort') : this.translate.instant('txScheduler.twoWeeks'),
				click: (mouseEvent, htmlElement) => {
					this.resetHeaderData();
					this.calendarComponent.getApi().changeView('resourceTimelineWeek2');
					this.calendarComponent.getApi().refetchEvents();
					this.updateDateRangeText();

					this.appConfig.planingConfig$.pipe(take(1)).subscribe((config) => {
						config.SelectedView = 'resourceTimelineWeek2';
						this.appConfig.setPlaningConfig(config);
					});

					htmlElement.parentNode.querySelector('.fc-state-active')?.classList.remove('fc-state-active');
					htmlElement.classList.add('fc-state-active');
				},
			},

			prevCustom: {
				text: '',
				bootstrapFontAwesome: 'fa-chevron-left',
				click: () => {
					const api = this.calendarComponent.getApi();
					const currentView = api.view.type;
					
					if (currentView === 'resourceTimelineWeek2') {
						// For 2-week view, navigate back only 1 week instead of 2
						api.gotoDate(moment(api.getDate()).subtract(1, 'week').toDate());
					} else {
						// For other views, use default navigation
						api.prev();
					}
					this.resetHeaderData();
					api.refetchEvents();
					this.updateDateRangeText();
				},
			},

			nextCustom: {
				text: '',
				bootstrapFontAwesome: 'fa-chevron-right',
				click: () => {
					const api = this.calendarComponent.getApi();
					const currentView = api.view.type;
					
					if (currentView === 'resourceTimelineWeek2') {
						// For 2-week view, navigate forward only 1 week instead of 2
						api.gotoDate(moment(api.getDate()).add(1, 'week').toDate());
					} else {
						// For other views, use default navigation
						api.next();
					}
					this.resetHeaderData();
					api.refetchEvents();
					this.updateDateRangeText();
				},
			}
		},
		resourceAreaWidth: this.deviceService.isMobile() ? '50%' : '22%',
		resourceAreaColumns: this.getResourceAreaColumns(),
		eventClick: ({ el, event, jsEvent, view }) => {
			if (this.DeleteMode) {
				if (this.SelectedForDelete.includes(event.id)) {
					this.SelectedForDelete = this.SelectedForDelete.filter(
						(s) => s !== event.id && !event.extendedProps.subEvents.map((e) => e.id).includes(s)
					);
					el.classList.remove('border-red');
				} else {
					this.SelectedForDelete.push(event.id, ...(event.extendedProps.subEvents?.map((e) => e.id) ?? []));
					el.classList.add('border-red');
				}
			}
		},
		eventDidMount: (info) => {
			if (!info.timeText) {
				info.timeText = `${moment(info.event.start).format('HH:mm')} - ${moment(info.event.end).format('HH:mm')}`;
			}
			const event_id = `event-tooltip-${info.event.id}`;
			if (!document.querySelector('#' + event_id)) {
				const tt = document.createElement('div');
				tt.id = event_id;
				tt.className = 'mdc-tooltip event-tooltip';
				tt.setAttribute('role', 'tooltip');
				tt.setAttribute('aria-hidden', 'true');

				const inner = tt.appendChild(document.createElement('div'));
				inner.className = 'mdc-tooltip__surface mdc-tooltip__surface-animation';
				inner.innerText = `${info.timeText}: ${info.event.title}`;

				document.body.appendChild(tt);

				info.el.setAttribute('aria-describedby', tt.id);

				new MDCTooltip(tt);
			}
		},
		weekNumbers: true,
		weekNumberCalculation: 'ISO',
		weekText: this.translate.instant('txScheduler.cw'),
		weekTextLong: this.translate.instant('txScheduler.week'),
		views: {
			resourceTimelineDay: {
				displayEventTime: true,
				eventTimeFormat: {
					hour: '2-digit',
					minute: '2-digit',
				},
				titleFormat: {
					day: '2-digit',
					month: 'long',
					weekday: 'long',
					year: 'numeric',
				},
				buttonText: this.translate.instant('txScheduler.day'),
				slotDuration: '01:00',
				slotLabelFormat: [
					{
						hour: '2-digit',
						minute: '2-digit',
					},
				],
				slotLabelContent: (hookProps) => hookProps.text.replace(' Uhr', ''),
				select: this.selectTimeRange.bind(this),
			},
			resourceTimelineWeek: {
				type: 'resourceTimeline',
				eventTimeFormat: {
					hour: '2-digit',
					minute: '2-digit',
				},
				titleFormat: {
					month: 'long',
					day: '2-digit',
					week: 'narrow',
					year: 'numeric',
				},
				buttonText: this.translate.instant('txScheduler.week'),
				slotDuration: { days: 1 },
				slotLabelFormat: {
					weekday: 'short',
					day: '2-digit',
					omitCommas: true,
				},
				select: this.selectTimeRange.bind(this),
				slotLabelContent: (arg) => {
					const dateKey = moment(arg.date).format('YYYY-MM-DD');
					const headerData = this.headerDataSubject.getValue();

					// Only fetch data once when the view is rendered
					if (Object.keys(headerData).length === 0 && !this.isLoadingHeaderData) {
						this.isLoadingHeaderData = true;

						const schedulerDateRange = {
							von: moment(this.calendarComponent.getApi().view.currentStart).format('YYYY-MM-DDTHH:mm:ss'),
							bis: moment(this.calendarComponent.getApi().view.currentEnd).format('YYYY-MM-DDTHH:mm:ss'),
							team_leader_for: this.teamLeaderFor,
						};

						this.api.callAPI('getCalendarHead', schedulerDateRange).pipe(
							take(1),
							map((res) => {
								const headerData = {};
								res.ListOfCalendarHead
									.filter(data => data.special_day !== null)
									.forEach(data => {
										const date = moment(data.datum).format('YYYY-MM-DD');
										headerData[date] = data.special_day;
									});
								return headerData;
							}),
							finalize(() => {
								this.isLoadingHeaderData = false;
							})
						).subscribe(headerData => {
							this.headerDataSubject.next(headerData);
						});
					}

					const specialDay = headerData[dateKey];
					if (specialDay) {
						return {
							html: `<span class="txc-cal-specialdaycolor">${arg.text}</span>`
						};
					}

					return arg.text;
				},
			},

			resourceTimelineWeek2: {
				type: 'resourceTimeline',
				duration: { weeks: 2 },
				eventTimeFormat: {
					hour: '2-digit',
					minute: '2-digit',
				},
				titleFormat: {
					month: 'long',
					day: '2-digit',
					week: 'narrow',
					year: 'numeric',
				},
				buttonText: this.translate.instant('txScheduler.twoWeeks'),
				slotDuration: { days: 1 },
				slotLabelFormat: {
					weekday: 'short',
					day: '2-digit',
					omitCommas: true,
				},
				select: this.selectTimeRange.bind(this),
				slotLabelContent: (arg) => {
					const dateKey = moment(arg.date).format('YYYY-MM-DD');
					const headerData = this.headerDataSubject.getValue();

					// Only fetch data once when the view is rendered
					if (Object.keys(headerData).length === 0 && !this.isLoadingHeaderData) {
						this.isLoadingHeaderData = true;

						const schedulerDateRange = {
							von: moment(this.calendarComponent.getApi().view.currentStart).format('YYYY-MM-DDTHH:mm:ss'),
							bis: moment(this.calendarComponent.getApi().view.currentEnd).format('YYYY-MM-DDTHH:mm:ss'),
							team_leader_for: this.teamLeaderFor,
						};

						this.api.callAPI('getCalendarHead', schedulerDateRange).pipe(
							take(1),
							map((res) => {
								const headerData = {};
								res.ListOfCalendarHead
									.filter(data => data.special_day !== null)
									.forEach(data => {
										const date = moment(data.datum).format('YYYY-MM-DD');
										headerData[date] = data.special_day;
									});
								return headerData;
							}),
							finalize(() => {
								this.isLoadingHeaderData = false;
							})
						).subscribe(headerData => {
							this.headerDataSubject.next(headerData);
						});
					}

					const specialDay = headerData[dateKey];
					if (specialDay) {
						return {
							html: `<span class="txc-cal-specialdaycolor">${arg.text}</span>`
						};
					}

					return arg.text;
				},
			},
		},
		firstDay: 1,
		height: '100%',
		stickyHeaderDates: true,
		scrollTime: '12:00',
		allDaySlot: true,
		defaultAllDay: true,
		editable: false,
		selectable: true,
		nowIndicator: true,
		events: [],
		resources: [],
	};

	public reports$: Observable<ReportEntity[]>;
	public loadingReport = false;

	constructor(
		private readonly store: Store,
		private calendarUtilService: CalendarUtilService,
		private readonly workflowService: WorkflowService,
		private readonly planService: PlansService,
		private readonly dialog: MatDialog,
		private readonly appConfig: AppConfigService,
		private readonly _ngZone: NgZone,
		private readonly router: Router,
		private deviceService: DeviceDetectorService,
		private route: ActivatedRoute,
		private readonly api: TxApiService,
		private readonly reportService: TxreportapiService,
		public myTabStateService: TabStateService,
		private translate: TranslateService
	) {

		if (this.deviceService.isMobile()){
			this.myTabStateService.onTabClose('f2');
		}

		this.reports$ = this.store.select(getReportList).pipe(
			map((reports) => reports.filter((report) => report.report_section === 'planning')),
			filter((reports) => reports.length > 0)
		);

		appConfig.planingConfig$.pipe(take(1)).subscribe((config) => {
			this.SelectedGroups = config.SelectedGroups;
			this.selectedGroups$.next(config.SelectedGroups);
			
			// Load saved user selection
			this.SelectedUsers = config.SelectedUsers || [];
			this.selectedUsers$.next(this.SelectedUsers);

			const cb = () => {
				this.calendarComponent.getApi().changeView(config.SelectedView ?? 'resourceTimelineDay');
			};

			if (this.calendarComponent) {
				cb();
				this.setCurrentViewState();
			} else {
				setTimeout(() => {
					cb();
					this.setCurrentViewState();
				}, 100);
			}
		});

		this.route.firstChild?.paramMap.pipe(take(1)).subscribe((params) => {
			const date = moment(params.get('date'));
			if (date.isValid()) {
				if (this.calendarComponent) {
					this.calendarComponent.getApi().gotoDate(date.toDate());
				} else {
					setTimeout(() => {
						this.calendarComponent.getApi().gotoDate(date.toDate());
					}, 100);
				}
			}
		});

		this.workflowGroups$ = workflowService.getWorkflowGroups();
		// Separate Observable für alle verfügbaren Nutzer (nur nach Gruppen gefiltert)
		const allAvailableUsers$ = combineLatest([
			this.store.select(getPlanCalendar), 
			this.selectedGroups$.asObservable()
		]).pipe(
			map(([res, selectedGroups]) => {
				// Nur nach Gruppen filtern für die Dropdown-Liste
				return res.filter((d) => (selectedGroups ?? []).includes(d.workflow_group));
			}),
			tap((res) => {
				// Alle verfügbaren Benutzer sammeln für das Dropdown (unabhängig von Nutzer-Auswahl)
				this.allUsers = res.map(g => ({
					value: g.employee_id,
					label: g.employee_name
				}));
				
				// Duplizierte Benutzer entfernen
				this.allUsers = this.allUsers.filter((user, index, self) => 
					index === self.findIndex(u => u.value === user.value)
				);
				
				// Sortieren nach Name
				this.allUsers.sort((a, b) => a.label.localeCompare(b.label));
				
				// Filterliste initial befüllen
				this.filteredUsersArray = [...this.allUsers];
			})
		);

		// Observable für den Kalender (nach Gruppen UND Benutzern gefiltert)
		this.workflows$ = combineLatest([
			allAvailableUsers$,
			this.selectedUsers$.asObservable()
		]).pipe(
			map(([res, selectedUsers]) => {
				// Filter nach Benutzern (wenn Benutzer ausgewählt sind)
				if (selectedUsers && selectedUsers.length > 0) {
					return res.filter((d) => selectedUsers.includes(d.employee_id));
				}
				
				// Wenn keine Benutzer ausgewählt sind, alle aus den ausgewählten Gruppen anzeigen
				return res;
			}),
			tap((res) => {
				this.CalendarOptions.resources = res.map(
					(g) => ({ id: `${g.employee_id}`, title: g.employee_name, info: g.info } as ResourceInput)
				);
				this.CalendarOptions.events = this.fetchEvents.bind(this);
			})
		);

		// Zusätzlich das allAvailableUsers$ Observable subscriben um sicherzustellen, dass allUsers aktualisiert wird
		allAvailableUsers$.subscribe();

		this.selectedGroups$.pipe(withLatestFrom(appConfig.planingConfig$)).subscribe(([selected, config]) => {
			if (JSON.stringify(selected.sort()) !== JSON.stringify(config.SelectedGroups.sort())) {
				config.SelectedGroups = selected;
				this.SelectedGroups = selected;
				this.appConfig.setPlaningConfig(config);
			}
		});

		// Save user selection to config
		this.selectedUsers$.pipe(withLatestFrom(appConfig.planingConfig$)).subscribe(([selected, config]) => {
			if (JSON.stringify(selected.sort()) !== JSON.stringify((config.SelectedUsers || []).sort())) {
				config.SelectedUsers = selected;
				this.SelectedUsers = selected;
				this.appConfig.setPlaningConfig(config);
			}
		});
	}

	private resetHeaderData(): void {
		this.headerDataSubject.next({});
		this.isLoadingHeaderData = false;
	}

	ngOnInit() {
		// Lies den "date"-Parameter aus der URL aus.
		this.route.paramMap.pipe(take(1)).subscribe((params) => {
			const dateParam = params.get('date');
			if (dateParam) {
				const date = moment(dateParam, 'YYYY-MM-DD');
				if (date.isValid() && this.calendarComponent) {
					// Den Kalender auf das angegebene Datum setzen.
					this.calendarComponent.getApi().gotoDate(date.toDate());
				} else {
					// Falls der Kalender noch nicht initialisiert wurde,
					// kann ein kleiner setTimeout helfen:
					setTimeout(() => {
						if (this.calendarComponent) {
							this.calendarComponent.getApi().gotoDate(date.toDate());
						}
					}, 100);
				}
			}
		});
	}

	onTabOpen(name :string) {
		this.myTabStateService.onTabOpen(name);
	}

	onTabClose(name :string) {
		this.myTabStateService.onTabClose(name);
	}

	// New method for view skeleton render
	viewSkeletonRender(info) {

	}

	onDateSelected(event: MatDatepickerInputEvent<Date>) {
		const selectedDate = moment(event.value);
		this.resetHeaderData();
		this.calendarComponent.getApi().gotoDate(selectedDate.toDate());
		this.router.navigateByUrl(`/planung/${selectedDate.format('YYYY-MM-DD')}`);
		this.datePicker.close();

		setTimeout(() => {
			this.updateDateRangeText();
		}, 50);
	}

	stopPropagation(event: Event) {
		event.stopImmediatePropagation();
		event.stopPropagation();
		return false;
	}

	toggleMultiSelect(id: string) {
		if (this.MultiSelectUserIds.includes(id)) {
			this.MultiSelectUserIds = this.MultiSelectUserIds.filter((s) => s !== id);
		} else {
			this.MultiSelectUserIds.push(id);
		}
	}

	async downloadReport(report: ReportEntity) {
		this.loadingReport = true;

		const data = await firstValueFrom(this.reportService.getReport(report));
		this.dialog.open(TxModalPdfComponent, {
			minHeight: 'calc(100vh - 90px)',
			maxWidth: '95vw',
			height: 'auto',
			width: '1000px',
			data: {
				blob: data,
				title: report.report_name,
			},
		});
		this.loadingReport = false;
	}

	setCurrentViewState(): void {
		// get current view type
		const viewType = this.calendarComponent.getApi().view.type;
		// change state of view type button, depending on which view is currently active
		if (viewType === 'resourceTimelineDay') {
			document.querySelector('.fc-viewDay-button').classList.add('fc-state-active');
		} else if (viewType === 'resourceTimelineWeek') {
			document.querySelector('.fc-viewWeek-button').classList.add('fc-state-active');
		} else if (viewType === 'resourceTimelineWeek2') {
			document.querySelector('.fc-viewWeek2-button').classList.add('fc-state-active');
		}
	}

	ngAfterViewInit() {
		// Handle your calendar component initialization here
		this.appConfig.planingConfig$.pipe(take(1)).subscribe((config) => {
			if (this.calendarComponent && this.calendarComponent.getApi()) {
				this.calendarComponent.getApi().changeView(config.SelectedView ?? 'resourceTimelineDay');
				this.setCurrentViewState();

				// Reset header data to force fresh load
				this.resetHeaderData();

				// Give calendar a moment to initialize before refetching events
				setTimeout(() => {
					this.calendarComponent.getApi().refetchEvents();
				}, 100);
			}
		});

		// Handle route parameter for date
		this.route.paramMap.pipe(take(1)).subscribe((params) => {
			const dateParam = params.get('date');
			if (dateParam) {
				const date = moment(dateParam, 'YYYY-MM-DD');
				if (date.isValid() && this.calendarComponent) {
					this.calendarComponent.getApi().gotoDate(date.toDate());
				}
			}
		});

		if (this.calendarComponent && this.calendarComponent.getApi()) {
			this.updateDateRangeText();

			// EventListener für Datumsänderungen hinzufügen
			this.calendarComponent.getApi().on('datesSet', () => {
				this._ngZone.run(() => {
					this.updateDateRangeText();
				});
			});
		} else {
			// Falls der Kalender noch nicht initialisiert wurde
			setTimeout(() => {
				if (this.calendarComponent && this.calendarComponent.getApi()) {
					this.updateDateRangeText();

					// EventListener für Datumsänderungen hinzufügen
					this.calendarComponent.getApi().on('datesSet', () => {
						this._ngZone.run(() => {
							this.updateDateRangeText();
						});
					});
				}
			}, 100);
		}
	}

	updateDateRangeText() {
		const view = this.calendarComponent.getApi().view;

		// Aktuelle Sprache aus TranslateService verwenden
		const currentLang = this.translate.currentLang || 'de';

		const start = moment(view.currentStart).locale(currentLang);
		const end = moment(view.currentEnd).locale(currentLang);

		if (view.type === 'dayGridMonth') {
			this.currentDateRangeText = start.format('MMMM YYYY');
		} else if (view.type === 'resourceTimelineDay') {
			// Für Tagesansicht: "Donnerstag, 05. Juni 2025"
			this.currentDateRangeText = start.format('dddd, DD. MMMM YYYY');
		} else if (view.type === 'resourceTimelineWeek' || view.type === 'resourceTimelineWeek2') {
			// Für Wochenansichten: 1 Tag vom Enddatum abziehen
			const adjustedEnd = moment(view.currentEnd).subtract(1, 'day').locale(currentLang);
			this.currentDateRangeText = start.format('D. MMM') + ' - ' + adjustedEnd.format('D. MMM YYYY');
		} else {
			this.currentDateRangeText = start.format('D. MMM') + ' - ' + end.format('D. MMM YYYY');
		}
	}

	/**
	 * Switches every group on or off at once.
	 *
	 * One emission regardless of how many groups change, because the reload hangs off
	 * selectedGroups$ rather than off the individual chip.
	 */
	setAllGroups(active: boolean, groups: ListOfWorkflowGroup[]): void {
		// Fresh array: the persist subscriber sorts it in place before storing.
		this.selectedGroups$.next(active ? (groups ?? []).map((group) => group.id) : []);
	}

	toggleChip(groupId: number): void {
		// Prüfen, ob die Gruppe bereits ausgewählt ist
		const index = this.SelectedGroups.indexOf(groupId);

		if (index >= 0) {
			// Wenn bereits ausgewählt, entferne sie
			const updatedGroups = [...this.SelectedGroups];
			updatedGroups.splice(index, 1);
			this.selectedGroups$.next(updatedGroups);
		} else {
			// Wenn nicht ausgewählt, füge sie hinzu
			const updatedGroups = [...this.SelectedGroups, groupId];
			this.selectedGroups$.next(updatedGroups);
		}
	}

	onUsersSelected(selectedUsers: number[]): void {
		this.SelectedUsers = selectedUsers || [];
		this.selectedUsers$.next(this.SelectedUsers);
	}

	onSearchUsers(event: any): void {
		const searchValue = this.searchControlUsers.value;
		if (!searchValue || searchValue.trim() === '') {
			this.filteredUsersArray = [...this.allUsers];
		} else {
			const searchTerm = searchValue.toLowerCase().trim();
			this.filteredUsersArray = this.allUsers.filter(user =>
				user.label.toLowerCase().includes(searchTerm)
			);
		}
	}

	clearUserFilter(): void {
		// Zurücksetzen der ausgewählten Benutzer
		this.SelectedUsers = [];
		this.selectedUsers$.next(this.SelectedUsers);
	}

	abortDeleteMode() {
		this.SelectedForDelete = [];
		document.querySelectorAll('.border-red').forEach((el) => el.classList.remove('border-red'));
		this.DeleteMode = !this.DeleteMode;
	}

	printCalendar() {
		console.log(document.getElementById('calendar'));

		const mywindow = window.open('', 'PRINT', 'height=400,width=600');

		mywindow.document.write('<html><head>');
		mywindow.document.write(document.getElementsByTagName('head')[0].innerHTML);
		mywindow.document.write('</head><body>');
		mywindow.document.write('<h1>' + document.title + '</h1>');
		mywindow.document.write(document.querySelector('#calendar').innerHTML);
		mywindow.document.write('</body></html>');

		mywindow.document.close(); // necessary for IE >= 10
		mywindow.focus(); // necessary for IE >= 10*/

		mywindow.print();
		//mywindow.close();

		return true;
	}

	toggleDeleteMode() {
		if (this.DeleteMode) {
			combineLatest(this.SelectedForDelete.map((id) => this.planService.delete(Number(id))))
				.pipe(
					mergeMap((res) => {
						return res;
					}),
					tap(() => {
						this.calendarComponent.getApi().refetchEvents();
					}),
					take(1)
				)
				.subscribe();
			this.SelectedForDelete = [];
			document.querySelectorAll('.border-red').forEach((el) => el.classList.remove('border-red'));
		}
		this.DeleteMode = !this.DeleteMode;
	}

	fetchEvents(info, success, failure) {
		console.log('fetchEvents called');

		// If we already have a subscription, unsubscribe from it
		if (this.eventsSubscription) {
			this.eventsSubscription.unsubscribe();
			this.eventsSubscription = null;
		}

		this.eventsSubscription = this.planService
			.fetchCalendarListRaw(
				moment(info.start).format('YYYY-MM-DDTHH:mm:ss'),
				moment(info.end).format('YYYY-MM-DDTHH:mm:ss'),
				true
			)
			.pipe(
				withLatestFrom(this.planService.fetchList()),
				map(async ([res, plans]) => {
					// Get all visible resources (employees) in the current calendar view
					let visibleResourceIds = [];

					// If calendar is initialized, get visible resources
					if (this.calendarComponent && this.calendarComponent.getApi()) {
						visibleResourceIds = this.calendarComponent
							.getApi()
							.getResources()
							.map((resource) => resource.id)
							.filter(Boolean);
					} else {
						// If calendar isn't initialized yet, consider all resources
						visibleResourceIds = res.map((r) => r.employee_id.toString());
					}

					// Filter resources to only include those currently visible in the calendar
					const filteredResources = res.filter((r) => visibleResourceIds.includes(r.employee_id.toString()));

					if (filteredResources.length === 0) {
						success([]);
						return;
					}

					// IMPORTANT: Create a Set of unique employee IDs to process
					const uniqueEmployeeIds = [...new Set(filteredResources.map((r) => r.employee_id.toString()))];

					console.log(`Processing ${uniqueEmployeeIds.length} unique employees`);

					// Track all promises for calendar head requests
					const calendarHeadPromises = [];
					const calendarHeadResults = {};

					// Process each unique employee ID
					for (const employeeId of uniqueEmployeeIds) {
						// Skip if we already have a pending request for this employee
						const requestKey = `${employeeId}_${info.start}_${info.end}`;
						if (this.pendingCalendarHeadRequests.has(requestKey)) {
							console.log(`Skipping duplicate request for employee ${employeeId}`);
							continue;
						}

						// Mark this request as pending
						this.pendingCalendarHeadRequests.add(requestKey);

						const resource = filteredResources.find((r) => r.employee_id.toString() === employeeId);
						if (!resource) continue;

						// Setup the calendar head request for this specific employee
						const schedulerDateRange = {
							von: moment(info.start).format('YYYY-MM-DDTHH:mm:ss'),
							bis: moment(info.end).format('YYYY-MM-DDTHH:mm:ss'),
							team_leader_for: resource.employee_id,
						};

						// Create a promise for this API call
						const promise = firstValueFrom(
							this.api.callAPI('getCalendarHead', schedulerDateRange).pipe(
								map((res) => {
									// Process the calendar head data for events
									const headData = (res.ListOfCalendarHead || [])
										.filter((schedulerData) => schedulerData.fehlzeit_name)
										.map((schedulerData) => {
											const datum = moment(schedulerData.datum).format('YYYY-MM-DD');
											const title = schedulerData.fehlzeit_name + schedulerData.info;
											return {
												id: schedulerData.id + 'FZ',
												title: title?.replace('null', '') ?? '',
												start: datum,
												end: datum,
												color: schedulerData.fehlzeit_color ? schedulerData.fehlzeit_color : '#dcdcdc',
												allDay: true,
												extendedProps: {
													type: 'FZ',
													fehlzeit_id: schedulerData.fehlzeit_id,
													fehlzeit_dauer: schedulerData.fehlzeit_dauer,
													fehlzeit_name: schedulerData.fehlzeit_name,
													clickable: !!schedulerData.fehlzeit_name,
												} as ListOfCalendarHead,
											};
										});

									// Also process special days for calendar headers
									const specialDays = (res.ListOfCalendarHead || []).filter((item) => item.special_day !== null);

									specialDays.forEach((schedulerData) => {
										const date = moment(schedulerData.datum).format('YYYY-MM-DD');
										this.calendarHeadData.set(date, {
											special_day: schedulerData.special_day,
											title: (schedulerData.fehlzeit_name ?? '') + (schedulerData.info ?? ''),
										});
									});

									// Convert specialDays to the format expected by the headerDataSubject
									if (specialDays && specialDays.length > 0) {
										const headerData = {};
										specialDays.forEach(schedulerData => {
											const date = moment(schedulerData.datum).format('YYYY-MM-DD');
											headerData[date] = schedulerData.special_day;
										});

										// Update the headerDataSubject with the new data
										// This ensures the data is available for slotLabelContent
										this.headerDataSubject.next({...this.headerDataSubject.getValue(), ...headerData});
									}

									// Update the currentViewDateRange to track the current date range
									this.currentViewDateRange = {
										start: moment(info.start).format('YYYY-MM-DDTHH:mm:ss'),
										end: moment(info.end).format('YYYY-MM-DDTHH:mm:ss')
									};

									// Force a re-render of the calendar to ensure special day formatting is applied
									// This is especially important for the initial load
									if (specialDays && specialDays.length > 0) {
										setTimeout(() => {
											if (this.calendarComponent && this.calendarComponent.getApi()) {
												this.calendarComponent.getApi().render();
											}
										}, 100);
									}

									// Store the result for this employee ID
									calendarHeadResults[employeeId] = headData;

									// Remove from pending requests
									this.pendingCalendarHeadRequests.delete(requestKey);

									return headData;
								}),
								catchError((err) => {
									console.error(`Error fetching calendar head for employee ${employeeId}:`, err);
									// Remove from pending requests even on error
									this.pendingCalendarHeadRequests.delete(requestKey);
									return of([]);
								})
							)
						);

						calendarHeadPromises.push(promise);
					}

					// Wait for all calendar head requests to complete
					await Promise.all(calendarHeadPromises);

					// Now process all resource data with the fetched calendar heads
					let data = [];

					for (const resource of filteredResources) {
						const employeeId = resource.employee_id.toString();

						// Get the calendar head data for this employee
						const calendarHead = calendarHeadResults[employeeId] || [];

						// Format calendar head events
						const formattedHeads = calendarHead.map((c) => ({
							id: c.id,
							resourceId: resource.employee_id,
							allDay: false,
							title: c.extendedProps.fehlzeit_name,
							start: moment(c.start).format('YYYY-MM-DDT00:00:00'),
							end: moment(c.end).format('YYYY-MM-DDT23:59:00'),
							timeZone: 'local',
							display: 'background',
							backgroundColor: c.color,
							borderColor: chroma(c.color).brighten(0.5).hex(),
							textColor: chroma(c.color).luminance() > 0.5 ? 'black' : 'white',
						}));

						// Format plan items
						const planItems =
							resource.ListOfPlanItem?.map((l) => {
								const start = moment(`${l.plan_date.split('T').reverse().pop()}T${l.begin_time.split('T').pop()}`);
								const end = moment(`${l.plan_date.split('T').reverse().pop()}T${l.end_time.split('T').pop()}`);
								const plan = plans.find((p) => p.id === l.plan_id);
								if (!plan) return null;

								const color = chroma(plan.color);
								return {
									id: l.id,
									resourceId: resource.employee_id,
									allDay: false,
									title: plan.name,
									start: start.format('YYYY-MM-DDTHH:mm:ss'),
									end: end.format('YYYY-MM-DDTHH:mm:ss'),
									timeZone: 'local',
									classNames: 'event-round-border',
									backgroundColor: plan.color,
									borderColor: color.brighten(0.5).hex(),
									textColor: color.luminance() > 0.5 ? 'black' : 'white',
								};
							}).filter(Boolean) || [];

						// Add all events for this resource to the data array
						data = [...data, ...formattedHeads, ...planItems];
					}

					// Process week view if needed (combining consecutive days)
					if (
						this.calendarComponent &&
						this.calendarComponent.getApi() &&
						(this.calendarComponent.getApi().view.type === 'resourceTimelineWeek' || 
					 this.calendarComponent.getApi().view.type === 'resourceTimelineWeek2')
					) {
						// aggregate following dates
						data = Array.from(
							data
								.reduce((acc, curr) => {
									if (!curr) return acc;

									const timeStartArr = curr.start.split('T');
									const timeEndArr = curr.end.split('T');
									const startTime = curr.start.split('T').pop();
									const endTime = curr.end.split('T').pop();

									const keyPrefix = `${curr.title}${curr.resourceId}${startTime}${endTime}`;
									const keys = Array.from(acc.keys()).filter((k: string) => k.startsWith(keyPrefix));

									if (keys.length === 0) {
										acc.set(keyPrefix + timeStartArr[0], {
											...curr,
											subEvents: [],
										});
									} else {
										let isInserted = false;
										for (const k of keys) {
											const el = acc.get(k);
											if (
												moment(curr.start).diff(moment(el.start), 'days') === 1 ||
												moment(curr.start).diff(moment(el.subEvents.slice().pop()?.start ?? el.start), 'days') === 1
											) {
												el.subEvents.push(curr);
												el.end = curr.end;
												isInserted = true;
												break;
											}
										}

										if (!isInserted) {
											acc.set(keyPrefix + timeStartArr[0], {
												...curr,
												subEvents: [],
											});
										}
									}
									return acc;
								}, new Map<string, any>())
								.values()
						);
					}

					success(data);
				}),
				catchError((err) => {
					console.error(err);
					failure(err);
					return of(err);
				}),
				take(1)
			)
			.subscribe();
	}

	removeEventTooltips() {
		document.querySelectorAll('.event-tooltip').forEach((el) => el.remove());
	}

	selectTimeRange(info: DateSelectArg) {
		this.openSelectPlanDialog(info.start, info.end, Number(info.resource.id));
	}

	openSelectPlanDialog(start: Date, end: Date, employeeId: number) {
		let checkedEnd = end;
		if (this.calendarComponent.getApi().view.type === 'resourceTimelineWeek') {
			checkedEnd = moment(end).subtract(1, 'days').toDate();
		}
		const dialog = this.dialog.open(TxPlaningSelectPlanDialogComponent, {
			data: {
				start,
				end: checkedEnd,
				view: this.calendarComponent.getApi().view.type,
			},
		});
		dialog
			.afterClosed()
			.pipe(
				filter(Boolean),
				withLatestFrom(this.store.select(getPlansList)),
				switchMap(([{ data, time }, plans]) => {
					const plan = plans.find((p) => p.id === data.selectedPlan);
					const override =
						time !== 'plan'
							? {
									start: moment(start).format(`YYYY-MM-DDT${data.selectedStart}:00`),
									end: moment(end).format(`YYYY-MM-DDT${data.selectedEnd === '00:00' ? '23:59' : data.selectedEnd}:00`),
							  }
							: {
									start: moment(start).format(`YYYY-MM-DDT${plan.begin_time.split('T').pop()}`),
									end: moment(end).format(
										`YYYY-MM-DDT${
											plan.end_time.split('T').pop() === '00:00:00' ? '23:59:00' : plan.end_time.split('T').pop()
										}`
									),
							  };
					if (this.MultiSelectUserIds.length > 0) {
						const ids = this.MultiSelectUserIds.map((i) => +i);
						if (!ids.includes(employeeId)) {
							ids.push(employeeId);
						}
						return forkJoin(
							this.planService.updateCalendarMulti(
								plan,
								ids,
								moment(data.startDate).format('YYYY-MM-DDTHH:mm:00'),
								moment(data.endDate).format('YYYY-MM-DDTHH:mm:00'),
								override
							)
						).pipe(
							tap(() => {
								(document.querySelector('.multi-user-select-all-cb') as HTMLInputElement).checked = false;
								document.querySelectorAll('.multi-user-select-cb').forEach((el: HTMLInputElement) => {
									el.removeAttribute('checked');
									el.checked = false;
								});
								this.MultiSelectUserIds = [];
							})
						);
					}
					return this.planService.updateCalendar(
						plan,
						employeeId,
						moment(data.startDate).format('YYYY-MM-DDTHH:mm:00'),
						moment(data.endDate).format('YYYY-MM-DDTHH:mm:00'),
						override
					);
				}),
				tap(() => this.calendarComponent.getApi().refetchEvents()),
				take(1)
			)
			.subscribe();
	}

	openPlansDialog() {
		this.dialog.open(TxPlaningPlansDialogComponent);
	}

	/**
	 * Get resource area columns configuration based on device type
	 * Hides info column on mobile devices and shows info in tooltip on name click
	 */
	private getResourceAreaColumns() {
		const columns: any[] = [
			{
				field: 'select',
				headerContent: (args) => {
					return createElement('input', {
						type: 'checkbox',
						className: 'multi-user-select-all-cb',
						onClick: (args) => {
							const el: HTMLInputElement = args.target;
							let selector = '.multi-user-select-cb';
							if (el.checked) {
								selector += ':not(:checked)';
							} else {
								selector += ':checked';
							}

							this.MultiSelectUserIds = [];

							document.querySelectorAll(selector).forEach((e: HTMLInputElement) => {
								e.click();
							});
						},
					});
				},
				width: 30,
				cellContent: (args) => {
					return createElement('input', {
						type: 'checkbox',
						className: 'multi-user-select-cb',
						onClick: () => {
							this.toggleMultiSelect(args.resource.id);
						},
					});
				},
			},
			{
				field: 'title',
				headerContent: this.translate.instant('txPlaning.name'),
				cellContent: (args) => {
					// On mobile, add click handler to show info tooltip
					if (this.deviceService.isMobile() && args.resource.extendedProps.info) {
						return createElement('span', {
							className: 'resource-title-mobile',
							innerText: args.resource.title,
							style: 'cursor: pointer; text-decoration: underline;',
							onClick: (e) => {
								e.stopPropagation();
								this.showInfoTooltip(e, args.resource.extendedProps.info, args.resource.id);
							},
						});
					}

					// Default title without click handler
					return createElement('span', {
						innerText: args.resource.title,
					});
				},
			},
		];

		// Add info column only on non-mobile devices
		if (!this.deviceService.isMobile()) {
			columns.push({
				field: 'info',
				headerContent: this.translate.instant('txPlaning.info'),
				cellContent: (args) => {
					return createElement('span', {
						dangerouslySetInnerHTML: {
							__html: args.resource.extendedProps.info,
						},
					});
				},
			});
		}

		return columns;
	}

	/**
	 * Show info tooltip on mobile when clicking on resource name
	 */
	private showInfoTooltip(event: MouseEvent, info: string, resourceId: string) {
		// Remove any existing info tooltip
		const existingTooltip = document.querySelector('.resource-info-tooltip');
		if (existingTooltip) {
			existingTooltip.remove();
		}

		// Create tooltip element
		const tooltip = document.createElement('div');
		tooltip.className = 'resource-info-tooltip';
		tooltip.style.position = 'fixed';
		tooltip.style.backgroundColor = '#616161';
		tooltip.style.color = 'white';
		tooltip.style.padding = '8px 12px';
		tooltip.style.borderRadius = '4px';
		tooltip.style.fontSize = '12px';
		tooltip.style.zIndex = '99999';
		tooltip.style.maxWidth = '300px';
		tooltip.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
		tooltip.style.wordWrap = 'break-word';
		tooltip.innerHTML = info;

		// Position tooltip
		document.body.appendChild(tooltip);

		const rect = (event.target as HTMLElement).getBoundingClientRect();
		const tooltipRect = tooltip.getBoundingClientRect();
		
		// Calculate position to keep tooltip in viewport
		let left = rect.left;
		let top = rect.bottom + 5;
		
		// Adjust if tooltip goes off right edge
		if (left + tooltipRect.width > window.innerWidth) {
			left = window.innerWidth - tooltipRect.width - 10;
		}
		
		// Adjust if tooltip goes off bottom edge
		if (top + tooltipRect.height > window.innerHeight) {
			top = rect.top - tooltipRect.height - 5;
		}
		
		tooltip.style.left = `${left}px`;
		tooltip.style.top = `${top}px`;

		// Remove tooltip when clicking anywhere else
		const removeTooltip = () => {
			tooltip.remove();
			document.removeEventListener('click', removeTooltip);
		};

		// Add delay to prevent immediate closing
		setTimeout(() => {
			document.addEventListener('click', removeTooltip);
		}, 100);
	}

	ngOnDestroy(): void {
		this.removeEventTooltips();

		// Clean up any existing subscriptions
		if (this.eventsSubscription) {
			this.eventsSubscription.unsubscribe();
			this.eventsSubscription = null;
		}
	}
}
